import React from 'react';
import { formatLabel } from 'utils/helpers';
import { 
  hasContent, 
  renderArray, 
  toxicityCardClass, 
  checkLD50, 
  checkLC50, 
  formatLD50Oral, 
  formatLD50Dermal, 
  formatLC50Inhalation 
} from './utils';

const ToxicitySection = ({ chemicalDetails }) => {
  const ld50OralData = formatLD50Oral(chemicalDetails);
  const ld50DermalData = formatLD50Dermal(chemicalDetails);
  const lc50InhalationData = formatLC50Inhalation(chemicalDetails);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 transition-all duration-300 hover:shadow-xl">
      <h2 className="text-2xl font-bold mb-6">
        <i className="fas fa-skull-crossbones mr-2"></i>Toxicity
      </h2>
      <div className="space-y-6">
        {/* Toxicity Values Table */}
        {chemicalDetails.toxicology?.toxicity_values?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Toxicity Exposure Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              {chemicalDetails.toxicology.toxicity_values.map((tox, idx) => {
                // Find category for value
                let category = null, catColor = 'bg-gray-200 text-gray-800';
                if (tox.type?.limits && Array.isArray(tox.type.limits)) {
                  for (const lim of tox.type.limits) {
                    if ((tox.value >= lim.lower) && (lim.upper === null || tox.value < lim.upper)) {
                      category = lim.category;
                      // Color by category
                      if (category === '1' || category === '2') catColor = 'bg-red-200 text-red-800';
                      else if (category === '3') catColor = 'bg-yellow-200 text-yellow-800';
                      break;
                    }
                  }
                }
                return (
                  <div key={tox.id} className={`rounded-lg p-6 shadow border ${catColor}`}>
                    <div className="flex flex-col gap-1">
                      <div className="font-bold text-lg">{tox.type?.name || 'Unknown'}</div>
                      <div className="text-sm italic text-gray-700 mb-2">{tox.type?.description}</div>
                      <div className="text-2xl font-bold">{tox.value} {tox.unit}</div>
                      {category && (
                        <div className="text-xs font-semibold mt-1">Category: {category}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LD50 Information (legacy, if present) */}
        {checkLD50(chemicalDetails) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* LD50 Oral */}
            {ld50OralData[0] && (
              <div className={`p-6 rounded-lg ${toxicityCardClass(ld50OralData[1])}`}>
                <div className="font-bold text-lg mb-2">LD50 (Oral)</div>
                <div className="text-2xl font-bold mb-2">{ld50OralData[0]}</div>
                <div className="text-sm">Toxicity Level: <span className="font-semibold">{ld50OralData[1]}</span></div>
              </div>
            )}

            {/* LD50 Dermal */}
            {ld50DermalData[0] && (
              <div className={`p-6 rounded-lg ${toxicityCardClass(ld50DermalData[1])}`}>
                <div className="font-bold text-lg mb-2">LD50 (Dermal)</div>
                <div className="text-2xl font-bold mb-2">{ld50DermalData[0]}</div>
                <div className="text-sm">Toxicity Level: <span className="font-semibold">{ld50DermalData[1]}</span></div>
              </div>
            )}

          </div>
        )}

        {/* LC50 Information */}
        {checkLC50(chemicalDetails) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* LC50 Inhalation */}
            {lc50InhalationData[0] && (
              <div className={`p-6 rounded-lg ${toxicityCardClass(lc50InhalationData[1])}`}>
                <div className="font-bold text-lg mb-2">LC50 (Inhalation)</div>
                <div className="text-2xl font-bold mb-2">{lc50InhalationData[0]}</div>
                <div className="text-sm">Toxicity Level: <span className="font-semibold">{lc50InhalationData[1]}</span></div>
              </div>
            )}

          </div>
        )}

        {/* Other Toxicity Information */}
        {chemicalDetails.toxic_release_measures && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(chemicalDetails.toxic_release_measures).map(([key, value]) => (
              key !== 'acute_toxicity' && hasContent(value) && (
                <div key={key} className="p-12 bg-gray-100 rounded-lg hover:bg-slate-300 transition-all">
                  <div className="font-bold text-lg">{formatLabel(key)}</div>
                  <div>
                    {renderArray(value)}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default ToxicitySection;
